import {useEffect, useCallback, useRef} from 'react';

import {useHistory, useLocation, useIsFirstRender, useLocalesMgr} from 'coreHooks';
import useChangeEffect from 'coreHooks/useChangeEffect';

import PlpUrlModifier from 'coreUtils/plp/url';
import ViewportMgr from 'coreClasses/view/ViewportMgr';
import SearchModel from 'coreClasses/search/SearchModel';
import AbstractSearchProvider from 'coreClasses/search/providers/AbstractSearchProvider';

import {FIRST_PAGE} from 'brandConstants';

interface UsePlpURLModifierParams {
	searchModel: SearchModel | null;
	searchMgr: AbstractSearchProvider;
	initialPage: number;
	currentUrl: string;
}

export const useMiniPLPURLModifier = () => {
	const history = useHistory();
	const location = useLocation();

	PlpUrlModifier.setLocation(location);
	PlpUrlModifier.setHistory(history);

	return null;
};

export default ({searchModel, searchMgr, initialPage, currentUrl}: UsePlpURLModifierParams) => {
	useEffect(() => {
		localesMgr.lockUrlUpdate(true);

		return () => {
			localesMgr.lockUrlUpdate(false);
		};
	}, []);

	const history = useHistory();
	const location = useLocation();
	const isFirstRender = useIsFirstRender();
	const localesMgr = useLocalesMgr();
	const currentLocale = localesMgr.getCurrentLocale();
	const pageUrlRef = useRef<string>(currentUrl);

	PlpUrlModifier.setLocation(location);
	PlpUrlModifier.setHistory(history);

	const setPageUrl = useCallback((url: string) => {
		pageUrlRef.current = url;
		searchMgr.setPageUrl(url);
	}, [searchMgr]);

	const translateRefinements = useCallback(
		(url: string) => {
			searchMgr.translateSelectedRefinements().then(() => {
				const userSelectedRefinements = searchMgr.getUserSelectedRefinements();
				const userSelectedRefinementsAdapted: Record<string, string> = {};

				Object.keys(userSelectedRefinements).forEach((refKey) => {
					if (userSelectedRefinements[refKey].length > 0) {
						userSelectedRefinementsAdapted[refKey] = userSelectedRefinements[refKey].join('|');
					}
				});

				const params: URLSearchParams = PlpUrlModifier.getUrlParameters(
					isFirstRender ? initialPage : FIRST_PAGE,
					searchMgr.getUserSelectedSorting(),
					userSelectedRefinementsAdapted,
				);

				const replaceUrl = location.isPop;
				localesMgr.updateUrl(url + '?' + params.toString(), true, replaceUrl);
			});
		},
		[initialPage, searchMgr],
	);

	// Only on changes: the PLP shown again by the keep-alive must keep its URL (page) and scroll.
	useChangeEffect(() => {
		if (!searchModel) {
			return;
		}

		PlpUrlModifier.updateUrl(
			pageUrlRef.current,
			FIRST_PAGE,
			searchModel.getSelectedSortingOption(),
			searchModel.getSelectedRefinements(),
		);

		ViewportMgr.scrollTo(0);
	}, [searchModel?.getSelectedSortingOption(), searchModel?.getSelectedRefinements()]);

	useChangeEffect(() => {
		if (currentUrl && !searchMgr.getSearchQuery() && localesMgr.isUrlUpdateLocked()) {
			translateRefinements(currentUrl);
		}
	}, [currentUrl]);

	useEffect(() => {
		if (currentUrl) {
			setPageUrl(currentUrl);
		}
	}, [currentUrl]);

	useChangeEffect(() => {
		if (searchMgr.getSearchQuery()) {
			const url: string = localesMgr.updateLinkWithLocale('/search');

			setPageUrl(url);
			translateRefinements(url);
		}
	}, [currentLocale.getCode()], true);

	return {setPageUrl};
};
